import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight, ChevronDown, Download, FileCode2, ListChecks, Loader2, ShieldCheck, Trash2, Unlink,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/shared/components/DeleteConfirmationDialog";
import BaseDrawer from "@/shared/components/Drawer";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogCancel,
} from "@/shared/ui/AlertDialog";
import { Button } from "@/shared/ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/Collapsible";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/shared/ui/Tabs";
import {
  broadcastBridgeTransferResult,
  clearBridgeTransferResult,
  openBridgeConsentWindow,
  watchBridgeConsentWindow,
} from "@/shared/utils/bridgeTransferChannel";
import { extractApiErrorDetail, toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { ACCOUNTING_QUERY_KEYS } from "../configs/queryKeys";
import { useBridgeAccountsQuery, useSettlementQuery, useSettlementsQuery } from "../hooks/useAccountingQueries";
import { accountingService } from "../services/accounting.service";
import { isTransferPaymentMode } from "../utils/paymentModes";
import BridgeTransferDialog from "./BridgeTransferDialog";
import SettlementAllocationsDrawer from "./SettlementAllocationsDrawer";
import AllocationsList from "./movement-allocations/AllocationsList";
import BankMovementEditor from "./movement-allocations/BankMovementEditor";
import CategoryManagerDrawer from "./movement-allocations/CategoryManagerDrawer";
import EcommerceAllocationDetail from "./movement-allocations/EcommerceAllocationDetail";
import EcommerceSearchDrawer from "./movement-allocations/EcommerceSearchDrawer";
import FusionPanel from "./movement-allocations/FusionPanel";
import LegacyActivityDetail from "./movement-allocations/LegacyActivityDetail";
import MoveToMovementDialog from "./movement-allocations/MoveToMovementDialog";
import MovementSettlementsTab from "./movement-allocations/MovementSettlementsTab";
import RefundSearchDrawer from "./movement-allocations/RefundSearchDrawer";
import ReimbursementAllocationDetail from "./movement-allocations/ReimbursementAllocationDetail";
import RulesManagerDrawer from "./movement-allocations/RulesManagerDrawer";
import SimpleAllocationEditor from "./movement-allocations/SimpleAllocationEditor";
import StockAllocationEditor from "./movement-allocations/StockAllocationEditor";
import { useMoveItems } from "./movement-allocations/useMoveItems";
import { useMovementAllocationsState } from "./movement-allocations/useMovementAllocationsState";
import { useMovementSettlementsTab } from "./movement-allocations/useMovementSettlementsTab";
import { extractApiErrorMessage } from "./movement-allocations/utils";
import type { BankMovement } from "../types/accounting.types";

type MovementAllocationsDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  bankMovement: BankMovement | null
  onBankMovementUpdated?: (movement: BankMovement) => void
  onBankMovementDeleted?: () => void
  highlightedAllocationId?: string | null
  width?: string
  onBack?: () => void
};

type DeleteMovementAllocationCounts = { linked: number, settled: number } | null;

const getDeleteMovementDescription = (counts: DeleteMovementAllocationCounts) => {
  if (!counts) {
    return "Ce mouvement bancaire sera supprimé ainsi que les écritures du grand livre qui lui sont liées. Cette action est irréversible.";
  }
  const { linked: linkedCount, settled: settledCount } = counts;
  const plural = linkedCount > 1;
  const linked = linkedCount > 0
    ? `Ce mouvement a ${linkedCount} écriture${plural ? "s" : ""} liée${plural ? "s" : ""} dans le grand livre. ${plural ? `Les ${linkedCount} écritures seront supprimées` : "L'écriture sera supprimée"} avec le mouvement.`
    : "Ce mouvement bancaire sera supprimé.";
  const settled = settledCount > 0
    ? ` ${settledCount > 1 ? `${settledCount} écritures rattachées à un règlement restent` : "1 écriture rattachée à un règlement reste"} au règlement.`
    : "";
  return `${linked}${settled} Cette action est irréversible.`;
};

const MovementAllocationsDrawer = ({
  open,
  onOpenChange,
  bankMovement,
  onBankMovementUpdated,
  onBankMovementDeleted,
  highlightedAllocationId,
  onBack,
  width = "893px",
}: MovementAllocationsDrawerProps) => {
  const queryClient = useQueryClient();

  const state = useMovementAllocationsState({
    open,
    onOpenChange,
    bankMovement,
    onBankMovementUpdated,
    onBankMovementDeleted,
  });

  const isMovementLocked = bankMovement?.locked ?? false;
  const linkedCountForDelete = state.deleteMovementAllocationCounts?.linked ?? 0;

  // Move items (selection mode)
  const moveItems = useMoveItems({
    allocations: state.allocations,
    bankMovementId: state.bankMovementId,
  });

  // Remise PDF download
  const hasRemittanceNumber = Boolean(bankMovement?.remittanceNumber);
  const { data: remiseSettlementsData } = useSettlementsQuery(
    { bankMovementIdEq: bankMovement?.id ?? "", limit: 1 },
    hasRemittanceNumber && Boolean(bankMovement?.id),
  );
  const hasRemiseSettlements = (remiseSettlementsData?.settlements?.length ?? 0) > 0;
  const showRemisePdfButton = hasRemittanceNumber && hasRemiseSettlements;
  const [isDownloadingRemisePdf, setIsDownloadingRemisePdf] = useState(false);

  const handleDownloadRemisePdf = useCallback(async () => {
    if (!bankMovement?.id) return;
    setIsDownloadingRemisePdf(true);
    try {
      const pdf = await accountingService.getRemisePdf(bankMovement.id);
      const bytes = Uint8Array.from(atob(pdf.fileBase), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdf.name;
      a.click();
      URL.revokeObjectURL(url);
    }
    catch (error) {
      toastErrorIfNotForbidden(error, "Erreur lors du téléchargement du bordereau de remise");
    }
    finally {
      setIsDownloadingRemisePdf(false);
    }
  }, [bankMovement?.id]);

  // Réédition des octets archivés, sans modifier la remise.
  const showSepaReEditButton = Boolean(bankMovement?.paymentNumber?.startsWith("MSG"));
  const [isReEditingSepa, setIsReEditingSepa] = useState(false);

  const handleSepaReEdit = useCallback(async () => {
    if (!bankMovement?.id) return;
    setIsReEditingSepa(true);
    try {
      const result = await accountingService.sepaExport({
        bankMovementIds: [bankMovement.id],
        process: "reEdit",
        dateExecution: new Date().toISOString().split("T")[0],
      });
      const cleaned = result.fileBase.replace(/\s/g, "");
      const bytes = Uint8Array.from(atob(cleaned), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: result.typeFile || "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.name || "virements_sepa.xml";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fichier SEPA conservé téléchargé à l’identique");
    }
    catch (err) {
      toastErrorIfNotForbidden(err, err instanceof Error ? err.message : "Erreur lors de la réédition SEPA");
    }
    finally {
      setIsReEditingSepa(false);
    }
  }, [bankMovement?.id]);

  // Bridge transfer from drawer
  const [bridgeDialogOpen, setBridgeDialogOpen] = useState(false);
  // ADM-726 : ne proposer le virement Bridge que si une banque connectée est
  // compatible (provider avec capability single_payment).
  const bridgeAccountsQuery = useBridgeAccountsQuery(state.serviceBridge);
  const hasTransferCapableBridge = (bridgeAccountsQuery.data?.bridgeAccounts ?? [])
    .some((account) => account.supports_transfer);
  // YTI-1732 laissait la section BRIDGE s'ouvrir sans le flag `service_bridge`
  // dès qu'il y avait quelque chose à fusionner, parce que le tableau proposait
  // ces fusions quel que soit le flag : le tiroir devait pouvoir les traiter.
  // Le tableau les masque désormais, donc la section suit le flag comme le
  // reste de Bridge. Une organisation qui perd l'option ne voit plus ni les
  // propositions ni le moyen de les traiter — c'est le sens de l'option.
  const isBridgeEligible = Boolean(
    bankMovement
    && state.serviceBridge
    && state.isPersistedMovement
    && !isMovementLocked
    && isTransferPaymentMode(bankMovement.paymentMode)
    && bankMovement.date === null
    && (bankMovement.debit ?? 0) > 0
    && bankMovement.clientIbanInfo
    && !bankMovement.brPaymentTransactionId
    && hasTransferCapableBridge,
  );

  // ADM-864 : une initiation sans statut bancaire peut être restée en l'air
  // (fenêtre bloquée ou fermée). Sans reprise, le mouvement était définitivement
  // figé en « Initié », donc non réinitiable.
  const isBridgeInitiationPending = Boolean(
    bankMovement
    && state.serviceBridge
    && !isMovementLocked
    && bankMovement.brPaymentTransactionId
    && !bankMovement.brStatus,
  );
  const [isResumeBusy, setIsResumeBusy] = useState(false);
  /**
   * Lien encore valide que le navigateur a refusé d'ouvrir : on le donne à
   * cliquer plutôt que de laisser l'élu devant un bouton sans effet visible.
   */
  const [blockedResumeUrl, setBlockedResumeUrl] = useState<string | null>(null);
  const resumeBridgeInitiation = useCallback(async () => {
    if (!bankMovement) return;
    setBlockedResumeUrl(null);
    setIsResumeBusy(true);
    try {
      const status = await accountingService.bridgePaymentStatus(bankMovement.id);

      if (status.consented) {
        toast.info("Le consentement a déjà été donné : le virement suit son cours chez Bridge.");
      }
      else if (status.consent_url) {
        // Le lien vit encore : le bouton fait ce que son libellé promet, il
        // rouvre la fenêtre sur le même payment request. Rien n'est créé, donc
        // aucun risque de virement en double.
        clearBridgeTransferResult(bankMovement.id);
        const consentWindow = openBridgeConsentWindow(status.consent_url);
        if (consentWindow) {
          watchBridgeConsentWindow(consentWindow, bankMovement.id);
          toast.success("Le lien de consentement a été rouvert");
        }
        else {
          // Fenêtre bloquée une seconde fois. Ne pas libérer pour autant :
          // l'initiation est encore consentable, et la libérer perdrait un lien
          // valide au lieu de régler le blocage du navigateur.
          setBlockedResumeUrl(status.consent_url);
        }
      }
      else {
        // Lien expiré : passé quinze minutes, Bridge renverrait l'élu sur notre
        // callback avec `reason=expired`. Il n'y a plus rien à rouvrir, donc on
        // libère la ligne.
        await accountingService.bridgeRelease(bankMovement.id);
        broadcastBridgeTransferResult({ movementId: bankMovement.id, phase: "released" });
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
        // Puis on enchaîne sur le récapitulatif, parce que l'élu a demandé à
        // payer, pas à débloquer une ligne. Le dialog, et non ce bouton, est ce
        // qui repartira chez Bridge : le mouvement reste modifiable entre deux
        // tentatives, donc le montant et le bénéficiaire doivent être relus
        // avant qu'un nouvel ordre ne sorte. C'est aussi lui qui sait afficher
        // les refus d'éligibilité, à leur place.
        setBridgeDialogOpen(true);
        toast.info("Lien de consentement expiré : vérifiez le virement, puis relancez-le.");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
    }
    catch (error) {
      toastErrorIfNotForbidden(
        error,
        extractApiErrorDetail(error) ?? "Impossible de vérifier l'état du virement auprès de Bridge.",
      );
    }
    finally {
      setIsResumeBusy(false);
    }
  }, [bankMovement, queryClient]);

  // Duplicate movement
  const [isDuplicateBusy, setIsDuplicateBusy] = useState(false);
  const hasSettlement = useMemo(
    () => state.allocations.some((a) => Boolean(a.settlementId)),
    [state.allocations],
  );

  const handleDuplicate = useCallback(async (date: string) => {
    if (!bankMovement) return;
    setIsDuplicateBusy(true);
    try {
      const newMovement = await accountingService.createBankMovement({
        date,
        label: bankMovement.label,
        accountId: bankMovement.accountId,
        paymentMode: bankMovement.paymentMode,
        debit: bankMovement.debit,
        credit: bankMovement.credit,
        paymentNumber: null,
        remittanceNumber: null,
        depositDate: null,
        issueDate: null,
      });
      if (!newMovement) throw new Error("Échec de la création du mouvement");

      for (const allocation of state.allocations) {
        await accountingService.createLedgerAllocation({
          bankMovementId: newMovement.id,
          companyAccountId: allocation.resolvedBankAccountId,
          date,
          budgetType: allocation.budgetType,
          operationType: allocation.operationType,
          operationTypeId: allocation.operationTypeId,
          description: allocation.description,
          debit: allocation.debit,
          credit: allocation.credit,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      toast.success("Mouvement dupliqué avec succès");

      if (onBankMovementUpdated) onBankMovementUpdated(newMovement);
    }
    catch (error) {
      // La duplication réécrit la date sur le mouvement ET sur chaque écriture :
      // un refus porte souvent sur une ligne précise, pas sur le mouvement.
      const description = extractApiErrorMessage(error);
      toastErrorIfNotForbidden(error, "Erreur lors de la duplication du mouvement", { description });
    }
    finally {
      setIsDuplicateBusy(false);
    }
  }, [bankMovement, state.allocations, queryClient, onBankMovementUpdated]);

  // Tabs
  const [activeTab, setActiveTab] = useState("ecritures");

  // Reset tab and selection mode when drawer closes
  const handleDrawerOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setActiveTab("ecritures");
      if (moveItems.isSelectionMode) moveItems.toggleSelectionMode();
    }
    state.handleDrawerOpenChange(nextOpen);
  }, [state.handleDrawerOpenChange, moveItems.isSelectionMode, moveItems.toggleSelectionMode]);

  // Settlements tab
  const settlementsTab = useMovementSettlementsTab({
    bankMovementId: state.bankMovementId,
    enabled: state.isPersistedMovement,
    isMovementLocked,
  });

  // Disassociate confirmation
  const [disassociateConfirmId, setDisassociateConfirmId] = useState<string | null>(null);
  const disassociateSettlement = disassociateConfirmId
    ? settlementsTab.associatedSettlements.find((s) => s.id === disassociateConfirmId)
    : null;
  const handleConfirmDisassociate = useCallback(async () => {
    if (!disassociateConfirmId) return;
    await settlementsTab.handleDisassociate(disassociateConfirmId);
    setDisassociateConfirmId(null);
  }, [disassociateConfirmId, settlementsTab.handleDisassociate]);

  const handleConfirmDeleteSettlement = useCallback(async () => {
    if (!disassociateConfirmId) return;
    await settlementsTab.handleDeleteSettlement(disassociateConfirmId);
    setDisassociateConfirmId(null);
  }, [disassociateConfirmId, settlementsTab.handleDeleteSettlement]);

  // Attach allocation to settlement
  const [isAttachingToSettlement, setIsAttachingToSettlement] = useState(false);
  const handleAttachToSettlement = useCallback(async (allocationId: string, settlementId: string) => {
    const allocation = state.allocations.find((a) => a.id === allocationId);
    if (!allocation) return;
    setIsAttachingToSettlement(true);
    try {
      await accountingService.updateLedgerAllocation(allocationId, {
        bankMovementId: allocation.bankMovementId ?? state.bankMovementId,
        settlementId,
        date: allocation.date ?? undefined,
        budgetType: allocation.budgetType ?? undefined,
        operationTypeId: allocation.operationTypeId ?? undefined,
        description: allocation.description ?? undefined,
        debit: allocation.debit ?? undefined,
        credit: allocation.credit ?? undefined,
      });
      toast.success("Écriture rattachée au règlement");
      state.closeSimpleEditor();
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      // Allocation changes can affect the status of linked entities
      queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
      queryClient.invalidateQueries({ queryKey: ["ecom-orders"] });
    }
    catch (error) {
      const description = extractApiErrorMessage(error);
      toastErrorIfNotForbidden(error, "Erreur lors du rattachement au règlement", { description });
    }
    finally {
      setIsAttachingToSettlement(false);
    }
  }, [state.allocations, state.bankMovementId, state.closeSimpleEditor, queryClient]);

  // Settlement drawer (3rd level)
  const [settlementDrawerId, setSettlementDrawerId] = useState<string | null>(null);
  const [settlementDrawerOpen, setSettlementDrawerOpen] = useState(false);
  const { data: settlementData } = useSettlementQuery(settlementDrawerId, Boolean(settlementDrawerId));
  const handleOpenSettlement = useCallback((id: string) => {
    setSettlementDrawerId(id);
    setSettlementDrawerOpen(true);
  }, []);

  return (
    <>
      <BaseDrawer
        drawerId="DRW-MVT"
        open={open}
        onOpenChange={handleDrawerOpenChange}
        onBack={onBack ? () => state.handleDrawerOpenChange(false, onBack) : undefined}
        isFirstLevel={!onBack}
        width={width}
        zIndex={60}
        bodyClassName="[&_*]:select-text"
        title={state.bankMovementId
          ? (
            <>
              Mouvement bancaire
              <span className="text-base font-normal text-slate-400">
                #
                {state.bankMovementId}
              </span>
            </>
          )
          : "Nouveau mouvement bancaire"}
        description="Éditez le mouvement bancaire et ses lignes d'affectation du grand livre."
      >
        <BankMovementEditor
          bankMovement={bankMovement}
          bankMovementId={state.bankMovementId}
          movementDraft={state.movementDraft}
          setMovementField={state.setMovementField}
          isMovementBusy={state.isMovementBusy}
          isPersistedMovement={state.isPersistedMovement}
          canSaveMovement={state.canSaveMovement}
          isBusy={state.isBusy}
          isCreatePending={state.isCreatePending}
          serviceBridge={state.serviceBridge}
          accountPopoverOpen={state.accountPopoverOpen}
          setAccountPopoverOpen={state.setAccountPopoverOpen}
          accountSearch={state.accountSearch}
          setAccountSearch={state.setAccountSearch}
          bankAccounts={state.bankAccounts}
          isBankAccountsLoading={state.isBankAccountsLoading}
          selectedAccount={state.selectedAccount}
          paymentModePopoverOpen={state.paymentModePopoverOpen}
          setPaymentModePopoverOpen={state.setPaymentModePopoverOpen}
          paymentModeSearch={state.paymentModeSearch}
          setPaymentModeSearch={state.setPaymentModeSearch}
          filteredPaymentModes={state.filteredPaymentModes}
          isPaymentModesLoading={state.isPaymentModesLoading}
          movementAttachment={state.movementAttachment}
          attachmentInputRef={state.attachmentInputRef}
          onUploadAttachment={state.handleUploadAttachment}
          onDeleteAttachment={state.handleDeleteAttachment}
          isUploadingAttachment={state.isUploadingAttachment}
          isDeletingAttachment={state.isDeletingAttachment}
          onSaveMovement={state.handleSaveMovement}
          onAddAllocation={state.handleAdd}
          onOpenRefundSearch={() => state.setRefundSearchOpen(true)}
          onOpenEcommerceSearch={() => state.setEcommerceSearchOpen(true)}
          onDeleteMovement={() => state.setIsDeleteMovementDialogOpen(true)}
          isDeleteMovementBusy={state.isDeleteMovementBusy}
          isMovementLocked={isMovementLocked}
          onDuplicate={handleDuplicate}
          isDuplicateBusy={isDuplicateBusy}
          hasSettlement={hasSettlement}
        />

        {(showRemisePdfButton || showSepaReEditButton || (state.isPersistedMovement && !isMovementLocked)) && (
          <div className="flex items-center gap-2 mb-3">
            {state.isPersistedMovement && !isMovementLocked && (
              <Button
                type="button"
                variant={moveItems.isSelectionMode ? "secondary" : "outline"}
                className="h-7 px-2.5 text-[11px] gap-1"
                onClick={moveItems.toggleSelectionMode}
              >
                <ListChecks className="h-3 w-3" />
                {moveItems.isSelectionMode ? "Quitter la sélection" : "Mode sélection"}
              </Button>
            )}
            {showRemisePdfButton && (
              <Button
                type="button"
                variant="outline"
                className="h-7 px-2.5 text-[11px] gap-1"
                onClick={handleDownloadRemisePdf}
                disabled={isDownloadingRemisePdf}
              >
                {isDownloadingRemisePdf
                  ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Génération...
                    </>
                  )
                  : (
                    <>
                      <Download className="h-3 w-3" />
                      Bordereau de remise
                    </>
                  )}
              </Button>
            )}
            {showSepaReEditButton && (
              <Button
                type="button"
                variant="outline"
                className="h-7 px-2.5 text-[11px] gap-1"
                onClick={handleSepaReEdit}
                disabled={isReEditingSepa}
              >
                {isReEditingSepa
                  ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Génération...
                    </>
                  )
                  : (
                    <>
                      <FileCode2 className="h-3 w-3" />
                      Rééditer SEPA XML
                    </>
                  )}
              </Button>
            )}
          </div>
        )}

        {!state.isPersistedMovement && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Enregistrez le mouvement avant d&apos;ajouter ou modifier des affectations.
          </div>
        )}

        {state.serviceBridge && state.isPersistedMovement && bankMovement && !isMovementLocked && (
          <div className="mb-3 space-y-2">
            {/* Bridge transfer button — always visible */}
            {isBridgeEligible && bankMovement.clientIbanInfo && (
              <div className="light-surface flex items-center justify-between rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">{bankMovement.clientIbanInfo.clientName}</div>
                    <div className="flex items-center gap-1 font-mono text-[10px] text-slate-500">
                      {bankMovement.clientIbanInfo.iban
                        ? `${bankMovement.clientIbanInfo.iban.slice(0, 4)}····${bankMovement.clientIbanInfo.iban.slice(-4)}`
                        : "-"}
                      {bankMovement.clientIbanInfo.ibanVerified && (
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="action"
                  size="sm"
                  className="h-7 text-[11px] gap-1.5 shrink-0"
                  onClick={() => setBridgeDialogOpen(true)}
                >
                  <ArrowUpRight className="h-3 w-3" />
                  Initier le virement
                </Button>
              </div>
            )}

            {isBridgeInitiationPending && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
                <p className="font-medium">Virement initié, en attente de validation bancaire</p>
                <p className="mt-0.5">
                  Si la fenêtre Bridge a été fermée ou bloquée, reprenez la validation : le lien de
                  consentement se rouvre pendant quinze minutes. Passé ce délai, le virement vous est
                  proposé à vérifier avant d&apos;être relancé.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1.5 h-7 text-[11px] gap-1.5"
                  onClick={resumeBridgeInitiation}
                  disabled={isResumeBusy}
                >
                  {isResumeBusy
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <ArrowUpRight className="h-3 w-3" />}
                  Reprendre le virement
                </Button>
                {blockedResumeUrl && (
                  <p className="mt-1.5">
                    Le navigateur a bloqué la fenêtre.
                    {" "}
                    <a
                      href={blockedResumeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium underline"
                    >
                      Ouvrir le lien de consentement
                    </a>
                  </p>
                )}
              </div>
            )}

            {state.serviceBridge && isTransferPaymentMode(bankMovement.paymentMode)
              && bankMovement.clientIbanInfo === null
              && (bankMovement.debit ?? 0) > 0 && !bankMovement.brPaymentTransactionId && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                Virement indisponible : plusieurs bénéficiaires différents ou aucun bénéficiaire identifié.
              </div>
            )}

            {/* Fusion section — collapsible */}
            {state.selectedAccount?.accountType !== "Caisse" && bankMovement.paymentMode !== "Espèces" && (
              <Collapsible className="rounded-md border border-slate-200 bg-white">
                <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 hover:bg-slate-50/50 transition-colors group">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Bridge</span>
                    {bankMovement.dateFusion && (
                      <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 border border-violet-200">
                        Fusionné
                      </span>
                    )}
                    {!bankMovement.dateFusion && state.fusionCandidates.length > 0 && (
                      <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 border border-violet-200 group-data-[state=open]:hidden">
                        {state.fusionCandidates.length}
                        {" "}
                        candidat(s)
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pb-3">
                  <FusionPanel
                    bankMovement={bankMovement}
                    fusionSearch={state.fusionSearch}
                    setFusionSearch={state.setFusionSearch}
                    fusionCandidates={state.fusionCandidates}
                    isFusionCandidatesLoading={state.isFusionCandidatesLoading}
                    onFuse={state.handleFuse}
                    onSplit={state.handleSplit}
                    isFusionBusy={state.isFusionBusy}
                    isSplitBusy={state.isSplitBusy}
                    fusingCandidateId={state.fusingCandidateId}
                    isBridgeMovement={state.isBridgeMovement}
                  />
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}

        {state.isPersistedMovement && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="ecritures" className="text-[12px]">
                Écritures
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-700">
                  {state.allocations.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="reglements" className="text-[12px]">
                Règlements
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-700">
                  {settlementsTab.associatedSettlements.length}
                </span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="ecritures">
              <AllocationsList
                allocations={state.allocations}
                isLoading={state.isLoading}
                getState={state.getState}
                setField={state.setField}
                isAllocationDirty={state.isAllocationDirty}
                onSave={state.handleSave}
                onDelete={state.deleteAllocation}
                isBusy={state.isBusy}
                operationPopoverAllocationId={state.operationPopoverAllocationId}
                setOperationPopoverAllocationId={state.setOperationPopoverAllocationId}
                operationPopoverSearchByAllocationId={state.operationPopoverSearchByAllocationId}
                setOperationPopoverSearchByAllocationId={state.setOperationPopoverSearchByAllocationId}
                operationSearchBusyByAllocationId={state.operationSearchBusyByAllocationId}
                markOperationSearchBusy={state.markOperationSearchBusy}
                operationTypesByBudget={state.operationTypesByBudget}
                onFindOrCreateOperationType={state.handleFindOrCreateOperationTypeForAllocation}
                isCreateOperationTypePending={state.isCreateOperationTypePending}
                onOpenSimpleEditor={(allocationId) => state.setSimpleEditorAllocationId(allocationId)}
                onOpenReimbursementEditor={(allocationId) => state.setReimbursementEditorAllocationId(allocationId)}
                onOpenEcommerceEditor={(allocationId) => state.setEcommerceEditorAllocationId(allocationId)}
                onOpenLegacyEditor={(allocationId) => state.setLegacyEditorAllocationId(allocationId)}
                onOpenStockEditor={(allocationId) => state.setStockEditorAllocationId(allocationId)}
                isCreatePending={state.isCreatePending}
                deletingAllocationId={state.deletingAllocationId}
                totalDebit={state.totalDebit}
                totalCredit={state.totalCredit}
                totalToAllocate={state.totalToAllocate}
                remainingToAllocate={state.remainingToAllocate}
                isSelectionMode={moveItems.isSelectionMode}
                selectedIds={moveItems.selectedAllocationIds}
                onToggleSelect={moveItems.toggleAllocationSelect}
                onToggleSelectAll={moveItems.toggleAllAllocationsSelect}
                selectedDebit={moveItems.selectedAllocationsDebit}
                selectedCredit={moveItems.selectedAllocationsCredit}
                onOpenMoveDialog={() => moveItems.openMoveDialog("allocations")}
                onClearSelection={moveItems.clearAllSelections}
                highlightedAllocationId={highlightedAllocationId}
              />
            </TabsContent>
            <TabsContent value="reglements">
              <MovementSettlementsTab
                associatedSettlements={settlementsTab.associatedSettlements}
                isLoadingAssociated={settlementsTab.isLoadingAssociated}
                searchQuery={settlementsTab.searchQuery}
                onSearchChange={settlementsTab.handleSearchChange}
                searchResults={settlementsTab.searchResults}
                isSearching={settlementsTab.isSearching}
                associatingId={settlementsTab.associatingId}
                disassociatingId={settlementsTab.disassociatingId}
                onAssociate={settlementsTab.handleAssociate}
                onDisassociate={(id) => setDisassociateConfirmId(id)}
                onOpenSettlement={handleOpenSettlement}
                isMovementLocked={isMovementLocked}
                isSelectionMode={moveItems.isSelectionMode}
                selectedSettlementIds={moveItems.selectedSettlementIds}
                onToggleSettlementSelect={moveItems.toggleSettlementSelect}
                onOpenMoveDialog={() => moveItems.openMoveDialog("settlements")}
                onClearSelection={moveItems.clearAllSelections}
              />
            </TabsContent>
          </Tabs>
        )}
      </BaseDrawer>

      <AlertDialog
        open={Boolean(disassociateConfirmId)}
        onOpenChange={() => {
          if (settlementsTab.disassociatingId || settlementsTab.deletingSettlementId) return;
          setDisassociateConfirmId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-4 w-full bg-amber-50 p-4 rounded-xl text-lg font-semibold text-amber-800 border border-amber-200">
              <Unlink size={24} className="text-amber-600 shrink-0" />
              Que faire de ce règlement
              {disassociateSettlement?.paymentMethod ? ` (${disassociateSettlement.paymentMethod})` : ""}
              {" "}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-3 text-sm text-slate-600">
              Choisissez l&apos;action à effectuer pour ce règlement et ses écritures.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-2">
            <button
              type="button"
              className="w-full text-left rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleConfirmDisassociate}
              disabled={Boolean(settlementsTab.disassociatingId || settlementsTab.deletingSettlementId)}
            >
              <div className="flex items-center gap-2 font-medium text-sm text-slate-800">
                {settlementsTab.disassociatingId
                  ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                  : <Unlink className="h-4 w-4 text-slate-500" />}
                Retirer le règlement de cette remise
              </div>
              <div className="mt-0.5 text-xs text-slate-500 ml-6">
                Le règlement et ses écritures seront dissociés du mouvement. Le règlement ne sera pas supprimé.
              </div>
            </button>
            <button
              type="button"
              className="w-full text-left rounded-lg border border-red-200 p-3 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleConfirmDeleteSettlement}
              disabled={Boolean(settlementsTab.disassociatingId || settlementsTab.deletingSettlementId)}
            >
              <div className="flex items-center gap-2 font-medium text-sm text-red-700">
                {settlementsTab.deletingSettlementId
                  ? <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                  : <Trash2 className="h-4 w-4 text-red-500" />}
                Supprimer le règlement
                {disassociateSettlement?.paymentMethod ? ` (${disassociateSettlement.paymentMethod})` : ""}
              </div>
              <div className="mt-0.5 text-xs text-slate-500 ml-6">
                Le règlement sera supprimé. Ses écritures d&apos;affectation resteront sur le mouvement bancaire.
              </div>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(settlementsTab.disassociatingId || settlementsTab.deletingSettlementId)}>
              Annuler
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteConfirmationDialog
        isOpen={state.isDeleteMovementDialogOpen}
        onClose={() => {
          if (state.isDeleteMovementBusy) return;
          state.setIsDeleteMovementDialogOpen(false);
        }}
        onConfirm={state.handleDeleteMovement}
        isLoading={state.isDeleteMovementBusy}
        itemName={state.bankMovementId || "ce mouvement"}
        title={linkedCountForDelete > 1
          ? "Attention : écritures liées"
          : linkedCountForDelete === 1 ? "Attention : écriture liée" : "Confirmer la suppression du mouvement"}
        description={getDeleteMovementDescription(state.deleteMovementAllocationCounts)}
        confirmLabel={state.isDeleteMovementBusy
          ? "Suppression..."
          : linkedCountForDelete > 0
            ? `Supprimer le mouvement et ${linkedCountForDelete > 1 ? `les ${linkedCountForDelete} écritures` : "l'écriture"}`
            : "Supprimer le mouvement"}
      />

      <SimpleAllocationEditor
        open={Boolean(state.simpleEditorAllocationId)}
        onClose={state.closeSimpleEditor}
        selectedAllocation={state.selectedSimpleAllocation}
        simpleEditorRow={state.simpleEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        isLoadingOperationTypes={state.isLoadingOperationTypes}
        isCreateOperationTypePending={state.isCreateOperationTypePending}
        operationTypeSearch={state.operationTypeSearch}
        setOperationTypeSearch={state.setOperationTypeSearch}
        simpleCategoryPopoverOpen={state.simpleCategoryPopoverOpen}
        setSimpleCategoryPopoverOpen={state.setSimpleCategoryPopoverOpen}
        simpleCategoryPopoverSearch={state.simpleCategoryPopoverSearch}
        setSimpleCategoryPopoverSearch={state.setSimpleCategoryPopoverSearch}
        markSimpleCategorySearchBusy={state.markSimpleCategorySearchBusy}
        simpleCategoryContainerRef={state.simpleCategoryContainerRef}
        operationTypes={state.operationTypes}
        supplierSearch={state.simpleEditorSupplierSearch}
        setSupplierSearch={state.setSimpleEditorSupplierSearch}
        suppliers={state.simpleEditorSuppliers}
        isLoadingSuppliers={state.isLoadingSimpleEditorSuppliers}
        selectedSupplierId={state.simpleEditorSelectedSupplierId}
        onSelectSupplier={(id, name) => {
          state.setSimpleEditorSelectedSupplierId(id);
          state.setSimpleEditorSupplierSearch(name);
          state.setIsSimpleEditorSupplierPopoverOpen(false);
        }}
        onClearSupplier={() => {
          state.setSimpleEditorSelectedSupplierId("");
          state.setSimpleEditorSupplierSearch("");
        }}
        isSupplierPopoverOpen={state.isSimpleEditorSupplierPopoverOpen}
        setIsSupplierPopoverOpen={state.setIsSimpleEditorSupplierPopoverOpen}
        supplierContainerRef={state.simpleEditorSupplierContainerRef}
        onSave={state.handleSaveSimpleEditor}
        onFindOrCreateOperationType={state.handleFindOrCreateOperationType}
        onOpenCategoryManager={() => state.setIsCategoryManagerOpen(true)}
        onOpenRulesManager={state.openRulesManager}
        onOpenSettlement={handleOpenSettlement}
        movementSettlements={settlementsTab.associatedSettlements}
        onAttachToSettlement={handleAttachToSettlement}
        isAttachingToSettlement={isAttachingToSettlement}
      />

      <ReimbursementAllocationDetail
        open={Boolean(state.reimbursementEditorAllocationId)}
        onClose={state.closeReimbursementEditor}
        selectedAllocation={state.selectedReimbursementAllocation}
        simpleEditorRow={state.reimbursementEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onSave={state.handleSaveReimbursementEditor}
        onOpenSettlement={handleOpenSettlement}
      />

      <EcommerceAllocationDetail
        open={Boolean(state.ecommerceEditorAllocationId)}
        onClose={state.closeEcommerceEditor}
        selectedAllocation={state.selectedEcommerceAllocation}
        simpleEditorRow={state.ecommerceEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onSave={state.handleSaveEcommerceEditor}
        onOpenSettlement={handleOpenSettlement}
      />

      <LegacyActivityDetail
        open={Boolean(state.legacyEditorAllocationId)}
        onClose={state.closeLegacyEditor}
        selectedAllocation={state.selectedLegacyAllocation}
        simpleEditorRow={state.legacyEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onSave={state.handleSaveLegacyEditor}
        onOpenSettlement={handleOpenSettlement}
      />

      {settlementDrawerOpen && (
        <SettlementAllocationsDrawer
          open={settlementDrawerOpen}
          onOpenChange={setSettlementDrawerOpen}
          settlement={settlementData ?? null}
          zIndex={65}
        />
      )}

      <StockAllocationEditor
        open={Boolean(state.stockEditorAllocationId)}
        onClose={state.closeStockEditor}
        bankMovementId={state.bankMovementId}
        allocation={state.selectedStockAllocation}
        isBusy={state.isBusy}
        onSave={state.handleSaveStockEditor}
      />

      <RefundSearchDrawer
        open={state.refundSearchOpen}
        onClose={() => state.setRefundSearchOpen(false)}
        isBusy={state.isBusy}
        onSelectOperation={state.handleAddRefund}
      />

      <EcommerceSearchDrawer
        open={state.ecommerceSearchOpen}
        onClose={() => state.setEcommerceSearchOpen(false)}
        isBusy={state.isBusy}
        onSelectOperation={state.handleAddEcommerceOperation}
      />

      <RulesManagerDrawer
        open={state.isRulesManagerOpen}
        onOpenChange={state.setIsRulesManagerOpen}
        ruleName={state.ruleName}
        setRuleName={state.setRuleName}
        ruleConditions={state.ruleConditions}
        updateRuleCondition={state.updateRuleCondition}
        addRuleCondition={state.addRuleCondition}
        removeRuleCondition={state.removeRuleCondition}
        ruleBudgetType={state.ruleBudgetType}
        setRuleBudgetType={state.setRuleBudgetType}
        ruleOperationTypeId={state.ruleOperationTypeId}
        setRuleOperationTypeId={state.setRuleOperationTypeId}
        ascOperationTypes={state.ascOperationTypes}
        aepOperationTypes={state.aepOperationTypes}
        bankAccounts={state.bankAccounts}
        isBankAccountsLoading={state.isBankAccountsLoading}
        ruleActionInProgress={state.ruleActionInProgress}
        onCreateRule={state.handleCreateLocalRule}
        onToggleRuleActive={state.handleToggleLocalRuleActive}
        onDeleteRule={state.handleDeleteLocalRule}
        sortedLocalRules={state.sortedLocalRules}
        isCategorizationRulesLoading={state.isCategorizationRulesLoading}
      />

      <CategoryManagerDrawer
        open={state.isCategoryManagerOpen}
        onOpenChange={state.setIsCategoryManagerOpen}
        bankMovementId={state.bankMovementId}
        selectedSimpleBudgetType={state.selectedSimpleBudgetType}
        categoryManagerFilter={state.categoryManagerFilter}
        setCategoryManagerFilter={state.setCategoryManagerFilter}
        operationTypes={state.operationTypes}
        inactiveOperationTypes={state.inactiveOperationTypes}
        isLoadingOperationTypes={state.isLoadingOperationTypes}
        isLoadingInactiveOperationTypes={state.isLoadingInactiveOperationTypes}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onDisableOperationType={state.handleDisableOperationType}
        onEnableOperationType={state.handleEnableOperationType}
        onRenameOperationType={state.handleRenameOperationType}
        onUpdateAccountNumber={state.handleUpdateAccountNumber}
        onMergeOperationType={state.handleMergeOperationType}
        onDeleteOperationType={state.handleDeleteOperationType}
        onOperationTypeRemoved={state.onOperationTypeRemoved}
      />

      <MoveToMovementDialog
        open={moveItems.isMoveDialogOpen}
        onOpenChange={moveItems.closeMoveDialog}
        sourceBankMovementId={state.bankMovementId}
        sourceMovement={bankMovement}
        type={moveItems.moveDialogType}
        selectedCount={moveItems.moveDialogType === "allocations" ? moveItems.selectedAllocationIds.size : moveItems.selectedSettlementIds.size}
        selectedDebit={moveItems.selectedAllocationsDebit}
        selectedCredit={moveItems.selectedAllocationsCredit}
        onConfirm={moveItems.handleConfirmMove}
        isMoving={moveItems.isMoving}
      />

      <BridgeTransferDialog
        open={bridgeDialogOpen}
        onOpenChange={setBridgeDialogOpen}
        movement={bankMovement}
      />
    </>
  );
};

export default MovementAllocationsDrawer;
